import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Repeat } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { ShopHero, ProductCard } from "../components/ShopUI";
import { fetchShopProducts } from "../lib/api";
import { money, useShop } from "../context/ShopContext";

const Perks = ({ fee }) => (
  <section className="bg-cream-alt border-y border-cream-border" data-testid="shop-perks">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3">
      {[
        { icon: Truck, title: "Flat-rate delivery", body: `${money(fee)} anywhere in Australia, tracked.` },
        { icon: ShieldCheck, title: "IFRA-compliant oils", body: "The same fragrances we supply to hotels and clinics." },
        { icon: Repeat, title: "Refill, don't replace", body: "Keep the hardware, top it up when it runs low." },
      ].map((p, i) => (
        <FadeUp key={p.title} delay={i * 0.08}
          className="flex items-start gap-4 py-10 px-6 md:px-8 border-b md:border-b-0 md:border-r border-cream-border last:border-0">
          <p.icon size={22} strokeWidth={1.3} className="text-gold shrink-0 mt-1" />
          <div>
            <h3 className="font-serif text-lg text-ink leading-snug">{p.title}</h3>
            <p className="text-sm text-ink-muted mt-1 leading-relaxed">{p.body}</p>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Group = ({ title, blurb, products, testid }) => (
  <section className="bg-cream" data-testid={testid}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
      <FadeUp className="mb-12 max-w-2xl">
        <h2 className="font-serif text-3xl md:text-[2.6rem] tracking-tight text-ink leading-tight">{title}</h2>
        <span className="block w-14 h-px bg-gold mt-5" />
        {blurb && <p className="text-ink-muted leading-relaxed mt-6">{blurb}</p>}
      </FadeUp>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
    </div>
  </section>
);

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useShop();

  useEffect(() => {
    fetchShopProducts()
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const kits = products.filter((p) => ["kit", "diffuser"].includes(p.category));
  const bundles = products.filter((p) => ["bundle", "subscription"].includes(p.category));
  const scents = products.filter((p) => p.category === "scent");
  const extras = products.filter((p) => p.category === "accessory");

  return (
    <div data-testid="shop-page">
      <ShopHero
        eyebrow="Shop"
        lines={["Bring it", "home."]}
        blurb="The same waterless diffusion and fragrance oils we install in hotels and clinics — sized for your car and your home. Flat-rate delivery Australia-wide."
      />
      <Perks fee={settings.delivery_fee} />

      {loading ? (
        <div className="bg-cream py-32 text-center text-ink-muted" data-testid="shop-loading">Loading the range…</div>
      ) : (
        <>
          {kits.length > 0 && (
            <Group testid="shop-kits" title="Diffusers & kits"
              blurb="Pick the setup that suits the space. Both arrive ready to run — nothing to assemble, no water to fill."
              products={kits} />
          )}
          {bundles.length > 0 && (
            <Group testid="shop-bundles" title="Bundles & refill plans"
              blurb="Buy three oils together and save, or put your refills on a monthly plan so the diffuser never runs dry."
              products={bundles} />
          )}
          {scents.length > 0 && (
            <Group testid="shop-scents" title="Signature fragrance oils"
              blurb="Each one blended for cold-air diffusion so the throw stays even and the top notes don't burn off."
              products={scents} />
          )}
          {extras.length > 0 && (
            <Group testid="shop-extras" title="Refills & extras" products={extras} />
          )}
        </>
      )}

      <section className="bg-[#221d17]" data-testid="shop-b2b-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="font-serif text-3xl md:text-4xl text-[#f4eee2] leading-tight">
              Scenting a venue, not a car?
            </h2>
            <p className="text-[#c9bda9] leading-relaxed mt-4">
              Commercial systems are sized and installed by our team. Tell us about the space and we'll come back with a recommendation.
            </p>
          </div>
          <Link to="/enquiries" data-testid="shop-enquiry-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#d8c9a3] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#e6dabb] transition-colors duration-300 shrink-0">
            Business Enquiries
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
